import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "./LoadingSpinner";
import { useAuthStore, type UserConfig } from "@/store/useAuthStore";
import { hasRole, isTeacher, type ValidRole } from "@/utils/role-utils";
import { useMyTrial } from "@/hooks/useMyTrial";
import { isTrialLocked } from "@/components/TrialBanner";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: ValidRole;
  requiredFlag?: keyof UserConfig;
  redirectTo?: string;
  allowWhenTrialLocked?: boolean;
}

const ProtectedRoute = ({
  children,
  requiredRole,
  requiredFlag,
  redirectTo,
  allowWhenTrialLocked = false,
}: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, isLoading: authLoading } = useAuthStore();
  const { trial, isDemo } = useMyTrial();

  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;

    // Reset redirect flag when auth state settles
    hasRedirectedRef.current = false;

    if (!isAuthenticated || !user) {
      if (!hasRedirectedRef.current) {
        hasRedirectedRef.current = true;
        navigate(redirectTo || "/login");
      }
      return;
    }

    if (requiredRole && !hasRole(user.role, requiredRole)) {
      if (!hasRedirectedRef.current) {
        hasRedirectedRef.current = true;
        const defaultRedirect = isTeacher(user.role)
          ? "/lessons"
          : "/classrooms";
        navigate(redirectTo || defaultRedirect);
      }
      return;
    }

    if (requiredFlag && !user.config?.[requiredFlag]) {
      if (!hasRedirectedRef.current) {
        hasRedirectedRef.current = true;
        const defaultRedirect = isTeacher(user.role)
          ? "/lessons"
          : "/classrooms";
        navigate(redirectTo || defaultRedirect);
      }
      return;
    }

    if (allowWhenTrialLocked) return;
    if (!trial) return;
    if (!isTrialLocked(trial)) return;
    if (!hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      const safeLanding = isTeacher(user.role) ? "/lessons" : "/classrooms";
      navigate(safeLanding);
    }
  }, [
    navigate,
    requiredRole,
    requiredFlag,
    redirectTo,
    isAuthenticated,
    user,
    authLoading,
    trial,
    allowWhenTrialLocked,
  ]);

  // SYNCHRONOUS render - only show loading during actual auth loading
  if (authLoading) {
    return (
      <LoadingSpinner message="Checking authentication..." placement="screen" />
    );
  }

  if (!isAuthenticated || !user) {
    return null; // Redirect happens via useEffect
  }

  if (requiredRole && !hasRole(user.role, requiredRole)) {
    return null; // Redirect happens via useEffect
  }

  if (requiredFlag && !user.config?.[requiredFlag]) {
    return null; // Redirect happens via useEffect
  }

  if (!allowWhenTrialLocked && isDemo && trial === undefined) {
    return <LoadingSpinner message="Checking access..." placement="screen" />;
  }

  if (!allowWhenTrialLocked && trial && isTrialLocked(trial)) {
    return null; // Redirect happens via useEffect
  }

  return <>{children}</>;
};

export default ProtectedRoute;
